import { Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { HeroBannerComponent } from '../Components/hero-banner/hero-banner.component';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormControl,
  Validators,
  FormArray,
  FormBuilder,
} from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms'; // Make sure to import this
import { FormService } from '../Services/form.service';

interface UploadedFile {
  file: File;
  uniqueFileName: string;
}

@Component({
  selector: 'app-contact-us-page',
  standalone: true,
  imports: [CommonModule, HeroBannerComponent, ReactiveFormsModule],
  templateUrl: './contact-us-page.component.html',
  styleUrl: './contact-us-page.component.scss',
})
export class ContactUsPageComponent {
  @ViewChild('formContainer', { static: false }) formContainer!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private formService: FormService,
    private renderer: Renderer2
  ) {}

  formSubmitted: boolean = false; // Track if the form is submitted
  isLoading: boolean = false; // Show a loading indicator during the submission process
  submissionError: boolean = false; // Track if there's an error

  servicesOptions: string[] = [
    'Media Walls',
    'Drinks Bars',
    'Home Offices',
    'Wardrobes',
    'Commercial Spaces',
    'Other',
  ];

  uploadedFiles: UploadedFile[] = []; // Holds the uploaded files
  maxFileSize = 10 * 1024 * 1024; // 10MB

  contactForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    timescale: new FormControl('', [Validators.required]),
    location: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
    ]),
    phone: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[0-9]+$/),
    ]),
    enquiry: new FormControl('', [Validators.required]),
    services: new FormArray(
      this.servicesOptions.map(() => new FormControl(false))
    ),
  });

  get servicesFormArray() {
    return this.contactForm.get('services') as FormArray;
  }

  onSubmit() {
    if (this.contactForm.valid) {
      // Reset error and loading states
      this.isLoading = true;
      this.submissionError = false;

      // Extract selected services
      const selectedServices =
        this.contactForm.value.services
          ?.map((checked, index) =>
            checked ? this.servicesOptions[index] : null
          )
          .filter((value) => value !== null) || [];

      // Create a FormData object to hold form data and files
      const formData = new FormData();

      // Append form fields to FormData
      formData.append('action', 'emailRequest');
      formData.append('name', this.contactForm.get('name')?.value || '');
      formData.append('email', this.contactForm.get('email')?.value || '');
      formData.append('phone', this.contactForm.get('phone')?.value || '');
      formData.append(
        'location',
        this.contactForm.get('location')?.value || ''
      );
      formData.append(
        'timescale',
        this.contactForm.get('timescale')?.value || ''
      );
      formData.append('enquiry', this.contactForm.get('enquiry')?.value || '');
      // Append services array as a stringified JSON array
      formData.append('services', JSON.stringify(selectedServices));

      // If there are uploaded files, upload them first
      if (this.uploadedFiles.length > 0) {
        this.uploadFiles()
          .then((imageUrls) => {
            // Create an array of objects with { name, url }
            const imgList = imageUrls.map((url, index) => ({
              name: this.uploadedFiles[index].uniqueFileName,
              url: url,
            }));

            // Append the imgList to the formData as a JSON string
            formData.append('imgList', JSON.stringify(imgList));

            // Once images are uploaded, submit the form data
            this.submitFormWithImages(formData);
          })
          .catch((error) => {
            console.error('Image upload failed', error);
            this.isLoading = false;
            this.submissionError = true;
          });
      } else {
        // No images, submit the form directly
        this.submitFormWithImages(formData);
      }
    }
  }

  // Method to scroll to the success message
  scrollToSuccessMessage() {
    if (this.formContainer) {
      const elementPosition =
        this.formContainer.nativeElement.getBoundingClientRect().top +
        window.scrollY;

      // Get the height of the viewport and subtract header height (e.g., 100px)
      const headerHeight = 350; // Adjust this value to your sticky header's height
      const viewportHeight = window.innerHeight;

      // Calculate the position to scroll to (middle of the viewport)
      const offsetPosition =
        elementPosition -
        viewportHeight / 2 +
        this.formContainer.nativeElement.offsetHeight / 2 -
        headerHeight;

      // Scroll smoothly to the calculated position
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }

  // Reset the form submission state to allow re-submission
  resetFormState() {
    this.formSubmitted = false;
    this.submissionError = false;
    this.contactForm.reset();
  }

  // Handle file input selection
  onFileSelected(event: any): void {
    const files = event.target.files;
    this.processFiles(files);
  }

  // Handle drag and drop files
  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(files);
    }
  }

  // Prevent default behavior for drag over
  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  // Maximum allowed files
  maxFiles = 3;
  maxTotalSize = 10 * 1024 * 1024; // 10MB cumulative size limit
  fileLimitReached: boolean = false;

  // Process the uploaded files
  processFiles(files: FileList | null): void {
    if (files) {
      const currentFileCount = this.uploadedFiles.length;
      const currentTotalSize = this.uploadedFiles.reduce(
        (acc, file) => acc + file.file.size,
        0
      );
      let totalSizeWithNewFiles = currentTotalSize;
      let filesAdded = 0;

      for (let i = 0; i < files.length; i++) {
        // Stop adding if max file count is reached
        if (currentFileCount + filesAdded >= this.maxFiles) {
          alert(`You can only upload a maximum of ${this.maxFiles} images.`);
          break;
        }

        const file = files[i];
        const predictedTotalSize = totalSizeWithNewFiles + file.size;

        // Stop adding if cumulative size limit is reached
        if (predictedTotalSize > this.maxTotalSize) {
          alert(
            `The total file size should not exceed 10MB. Current size: ${(
              totalSizeWithNewFiles /
              (1024 * 1024)
            ).toFixed(2)} MB.`
          );
          break;
        }

        // Generate a unique filename for each file
        const timestamp = Date.now();
        const randomString = this.generateRandomString(6);
        const fileExtension = file.name.split('.').pop();
        const uniqueFileName = `${timestamp}-${randomString}.${fileExtension}`;

        const uploadedFile: UploadedFile = {
          file, // Store the original file object
          uniqueFileName, // Store the unique filename
        };

        this.uploadedFiles.push(uploadedFile);
        totalSizeWithNewFiles += file.size;
        filesAdded++;
      }

      // Update fileLimitReached if the max file count or size limit is reached
      this.fileLimitReached =
        this.uploadedFiles.length >= this.maxFiles ||
        totalSizeWithNewFiles >= this.maxTotalSize;
    }
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.fileLimitReached = this.uploadedFiles.length >= this.maxFiles;
  }

  // Upload file to Azure Blob Storage
  uploadFiles(): Promise<string[]> {
    const uploadPromises = this.uploadedFiles.map((uploadedFile) => {
      return new Promise<string>((resolve, reject) => {
        const blobName = uploadedFile.uniqueFileName;

        // Get SAS URL for each img.
        this.formService.getSasUrl(blobName).subscribe({
          next: (response) => {
            const sasUrl = response.sasUrl;

            // Upload file to Azure using the SAS URL
            const headers = new Headers({ 'x-ms-blob-type': 'BlockBlob' });
            fetch(sasUrl, {
              method: 'PUT',
              headers: headers,
              body: uploadedFile.file,
            })
              .then((res) => {
                if (res.ok) {
                  console.log(`${blobName} uploaded successfully`);
                  resolve(sasUrl.split('?')[0]); // Return the URL without the SAS token
                } else {
                  reject(new Error(`Failed to upload ${blobName}`));
                }
              })
              .catch((err) => reject(err));
          },
          error: (err) => reject(err),
        });
      });
    });

    // Return a Promise that resolves when all files are uploaded and their URLs are returned
    return Promise.all(uploadPromises);
  }

  // Method to upload file using SAS URL
  uploadToAzureBlob(sasUrl: string, file: File): void {
    const headers = new Headers({ 'x-ms-blob-type': 'BlockBlob' });
    fetch(sasUrl, {
      method: 'PUT',
      headers: headers,
      body: file,
    })
      .then((response) => {
        if (response.ok) {
          console.log(`${file.name} uploaded successfully`);
        } else {
          console.error(`Failed to upload ${file.name}`);
        }
      })
      .catch((error) => {
        console.error('Upload error:', error);
      });
  }

  private submitFormWithImages(formData: FormData): void {
    // Send the form data to the backend using the service
    this.formService.submitForm(formData).subscribe({
      next: (response) => {
        if (response.result?.status === 'success') {
          this.isLoading = false;
          this.formSubmitted = true; // Show success message

          // Scroll to the top of the form container or success message
          this.scrollToSuccessMessage();

          globalThis.dataLayer.push({
            event: 'formSubmission', // Event name for GTM trigger
            // TODO: Add back in.
            // formId: 'contactUsForm', // Optional identifier for the form
            // formName: 'Contact Us Form', // Optional form name
            // submissionStatus: 'success', // You can add extra fields if needed
          });
        } else if (response.result.status === 'failed') {
          this.isLoading = false; // Handle error state
          this.submissionError = true; // Show error message
        }
      },
      error: (error) => {
        this.isLoading = false; // Handle error state
        this.submissionError = true; // Show error message
      },
    });
  }

  // Utility to generate a random string of 6 characters
  generateRandomString(length: number): string {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }
}
